/** Hook for Expert Chat SSE streaming + conversation management. */

import { useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { expertApi } from '../services/expertApi';
import { createSentenceChunker } from '../utils/sentenceChunker';
import { extractSpeechText } from '../utils/extractSpeechText';
import type { ExpertMessage, StreamingMessage, ToolExecution, SSECardData } from '../types/expert';
import type { SSEEvent } from '../types/chat';
import type { ActiveSpeakerState } from '../contexts/ActiveSpeakerContext';

interface RawSSEEvent {
  data: string;
}

function parseSSEBuffer(buffer: string): { events: RawSSEEvent[]; remainder: string } {
  const events: RawSSEEvent[] = [];
  const blocks = buffer.split('\n\n');
  const remainder = blocks.pop() || '';

  for (const block of blocks) {
    if (!block.trim()) continue;
    for (const line of block.split('\n')) {
      if (line.startsWith('data: ')) {
        events.push({ data: line.slice(6) });
        break;
      }
    }
  }

  return { events, remainder };
}

interface UseExpertStreamOptions {
  /** Active speaker context for voice mode (Story v19-1-2). */
  tts?: ActiveSpeakerState;
  /** Whether voice mode is enabled. */
  voiceMode?: boolean;
  /** H1 fix: auto-play TTS when a Banker message arrives (AC3/AC6).
   *  Set to true when banker_voice_autoplay preference is enabled. */
  bankerAutoplay?: boolean;
  /** Voice to use for Banker auto-play. */
  bankerVoice?: string;
  /** Voice to use when voice mode (analyst) is active. */
  analystVoice?: string;
  /** Selected persona slug to pass to the chat API (Story v19-1-6). */
  personaSlug?: string;
  /** Called when the conversation topic/intent changes mid-task (Story v19-2-2). */
  onContextChange?: (prevIntent: string, newIntent: string) => void;
}

export function useExpertStream(options?: UseExpertStreamOptions) {
  const [messages, setMessages] = useState<ExpertMessage[]>([]);
  const [streamingMessage, setStreamingMessage] = useState<StreamingMessage | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const queryClient = useQueryClient();

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const loadConversation = useCallback(async (conversationId: number) => {
    try {
      const detail = await expertApi.getConversation(conversationId);
      setMessages(detail.messages);
      setError(null);

      // H1 fix (AC3/AC6): auto-play the most recent Banker message on load
      // when banker_voice_autoplay preference is enabled.
      if (options?.bankerAutoplay && options?.tts) {
        const lastMsg = detail.messages[detail.messages.length - 1];
        if (
          lastMsg &&
          lastMsg.role === 'assistant' &&
          (lastMsg as ExpertMessage).metadata?.source === 'banker'
        ) {
          const speechText = extractSpeechText(lastMsg.content || '');
          if (speechText) {
            options.tts.speak(speechText, 'banker', (lastMsg as ExpertMessage).id, options.bankerVoice);
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conversation');
    }
  }, [options?.bankerAutoplay, options?.tts, options?.bankerVoice]);

  const sendMessage = useCallback(async (
    message: string,
    conversationId?: number,
  ) => {
    setIsStreaming(true);
    setError(null);

    // TTS is active when either voice mode is toggled on OR autoplay is enabled
    const ttsActive = !!(options?.voiceMode || options?.bankerAutoplay);

    // Clear TTS queue when sending a new message (interruption)
    if (options?.tts && ttsActive) {
      options.tts.stop();
    }

    // Add user message optimistically
    const userMsg: ExpertMessage = {
      id: Date.now(),
      role: 'user',
      content: message,
      tool_name: null,
      tool_args: null,
      tool_result: null,
      metadata: null,
      tokens_used: null,
      cost_usd: null,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMsg]);
    setStreamingMessage({ role: 'assistant', content: '', isStreaming: true, tools: [], cards: [] });

    // Create sentence chunker when voice mode is on OR autoplay is enabled
    const chunker = ttsActive ? createSentenceChunker() : null;
    const streamMessageId = Date.now() + 1; // stable ID for queue

    try {
      const controller = new AbortController();
      abortRef.current = controller;

      const response = await expertApi.sendMessageStream(
        message,
        conversationId,
        controller.signal,
        options?.personaSlug,
      );
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';
      let finalMessageId: number | null = null;
      const activeTools: ToolExecution[] = [];
      const cards: SSECardData[] = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const { events, remainder } = parseSSEBuffer(buffer);
        buffer = remainder;

        for (const event of events) {
          try {
            const parsed = JSON.parse(event.data) as SSEEvent;

            switch (parsed.t) {
              case 'chunk':
                fullContent += parsed.v;
                setStreamingMessage({
                  role: 'assistant',
                  content: fullContent,
                  isStreaming: true,
                  tools: [...activeTools],
                  cards: [...cards],
                });

                // Voice mode: feed chunk to sentence chunker
                if (chunker && options?.tts && parsed.v) {
                  const sentences = chunker.feed(parsed.v);
                  for (const sentence of sentences) {
                    const speechText = extractSpeechText(sentence);
                    if (speechText) {
                      options.tts.speak(speechText, 'analyst', streamMessageId, options.analystVoice);
                    }
                  }
                }
                break;

              case 'tool_start':
                activeTools.push({ name: parsed.name, status: 'calling' });
                setStreamingMessage(prev => prev ? {
                  ...prev,
                  tools: [...activeTools],
                } : prev);
                break;

              case 'tool_end': {
                const idx = activeTools.findIndex(t => t.name === parsed.name && t.status === 'calling');
                if (idx >= 0) {
                  activeTools[idx] = { name: parsed.name, status: 'result', summary: parsed.result };
                }
                setStreamingMessage(prev => prev ? {
                  ...prev,
                  tools: [...activeTools],
                } : prev);
                break;
              }

              case 'card':
                cards.push({ component: parsed.component, data: parsed.data });
                setStreamingMessage(prev => prev ? {
                  ...prev,
                  cards: [...cards],
                } : prev);
                break;

              case 'done':
                finalMessageId = parsed.message_id;
                break;

              case 'error':
                setError(parsed.message || 'An error occurred');
                break;

              case 'context_change':
                options?.onContextChange?.(parsed.prev_intent, parsed.new_intent);
                break;
            }
          } catch {
            // Skip malformed JSON
          }
        }
      }

      // Flush remaining sentence for voice mode
      if (chunker && options?.tts) {
        const remaining = chunker.flush();
        if (remaining) {
          const speechText = extractSpeechText(remaining);
          if (speechText) {
            options.tts.speak(speechText, 'analyst', streamMessageId, options.analystVoice);
          }
        }
      }

      // Finalize: replace streaming message with real message
      if (fullContent) {
        const assistantMsg: ExpertMessage = {
          id: finalMessageId || Date.now() + 1,
          role: 'assistant',
          content: fullContent,
          tool_name: null,
          tool_args: null,
          tool_result: null,
          metadata: null,
          tokens_used: null,
          cost_usd: null,
          created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, assistantMsg]);
      }

      // Invalidate conversation list cache
      queryClient.invalidateQueries({ queryKey: ['expert-conversations'] });

    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        setError(err.message);
      }
    } finally {
      setStreamingMessage(null);
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [queryClient, options?.tts, options?.voiceMode, options?.bankerAutoplay]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setStreamingMessage(null);
    setError(null);
  }, []);

  return {
    messages,
    streamingMessage,
    isStreaming,
    error,
    sendMessage,
    loadConversation,
    clearMessages,
    stopStreaming,
  };
}
